import { useNavigate, useParams } from "react-router-dom";
import { HashLoader } from "react-spinners";
import { 
  AirplaneInFlight, 
  CaretLeft, 
  Users, 
  Info, 
  Receipt, 
  Clock, 
  CheckCircle,
  Warning
} from "@phosphor-icons/react";
import moment from "moment";
import useFlightBooking from "../../hooks/useFlightBooking";
import { getLast } from "../../helpers/array";

const FlightDetails = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { flight, fetchingFlightBooking } = useFlightBooking(id);

  if (fetchingFlightBooking) {
    return (
      <div className="flex flex-col items-center justify-center py-32 gap-4">
        <div className="relative">
          <HashLoader size={48} color="#1A1A31" />
          <div className="absolute inset-0 animate-ping rounded-full bg-deepBlue/5" />
        </div>
        <p className="text-deepBlue font-bold text-xs uppercase tracking-widest animate-pulse">
          Retrieving Flight Details...
        </p>
      </div>
    );
  }

  if (!flight) {
    return (
      <div className="p-10 flex flex-col items-center justify-center gap-4 text-center">
        <div className="p-4 bg-red-50 rounded-full text-red-500">
          <Warning size={32} weight="fill" />
        </div>
        <h3 className="text-xl font-bold text-deepBlue">Flight Not Found</h3>
        <p className="text-gray-500 max-w-sm">
          We couldn't find the details for this flight booking. It might have been canceled or the ID is incorrect.
        </p>
        <button
          onClick={() => navigate(-1)}
          className="mt-4 px-8 py-3 bg-deepBlue text-white font-bold rounded-2xl hover:bg-deepBlue/90 transition-all"
        >
          Go Back
        </button>
      </div>
    );
  }

  const firstLeg = flight.flight_sets?.[0]?.FlightEntries?.[0];
  const lastLeg = getLast(flight.flight_sets ?? [])?.FlightEntries?.[0];
  const isPending = flight.type === "pending"; // Adjust based on actual data structure

  return (
    <div className="relative w-full flex gap-8 px-5 md:px-10 py-8 flex-col max-w-5xl mx-auto">
      {/* Header */}
      <div className="flex items-center justify-between gap-4">
        <div className="flex items-center gap-4">
          <button
            onClick={() => navigate(-1)}
            className="p-2.5 bg-white border border-gray-100 rounded-xl hover:bg-gray-50 transition-all"
          >
            <CaretLeft size={20} weight="bold" className="text-deepBlue" />
          </button>
          <div>
            <h1 className="text-2xl font-bold text-deepBlue">Flight Details</h1>
            <p className="text-sm text-gray-400 font-medium">
              Ref: <span className="text-gray-600">{flight.reference_number || flight.id.slice(0, 8)}</span>
            </p>
          </div>
        </div>

        <div className={`px-4 py-2 rounded-full text-xs font-bold uppercase tracking-wider flex items-center gap-2 ${
          isPending 
            ? 'bg-amber-50 text-amber-700 border border-amber-100' 
            : 'bg-green-50 text-green-700 border border-green-100'
        }`}>
          {isPending ? <Clock weight="fill" size={14} /> : <CheckCircle weight="fill" size={14} />}
          {isPending ? 'Pending Approval' : 'Confirmed'}
        </div>
      </div>

      {/* Main Ticket Info */}
      <div className="bg-white rounded-3xl border border-gray-100 shadow-sm overflow-hidden flex flex-col">
        {/* Accent Bar */}
        <div className={`h-2 w-full ${isPending ? 'bg-amber-400' : 'bg-visaro-orange'}`} />
        
        <div className="p-8 md:p-12 flex flex-col gap-10">
          {/* Route Section */}
          <div className="flex flex-col md:flex-row items-center justify-between gap-12">
            {/* Departure */}
            <div className="flex flex-col items-center md:items-start text-center md:text-left flex-1">
              <span className="text-gray-400 text-xs font-bold uppercase tracking-[0.2em] mb-2">Departure</span>
              <span className="text-5xl md:text-6xl font-bold font-aeonic text-deepBlue leading-none">
                {firstLeg?.DepartureAirportCode}
              </span>
              <span className="text-lg font-medium text-gray-600 mt-2">
                {firstLeg?.DepartureAirportName}
              </span>
              <div className="mt-4 flex flex-col">
                <span className="text-sm font-bold text-deepBlue">
                  {moment(firstLeg?.DepartureDate).format("ddd, MMM DD, YYYY")}
                </span>
                <span className="text-2xl font-bold text-visaro-orange">
                  {moment(firstLeg?.DepartureDate).format("HH:mm")}
                </span>
              </div>
            </div>

            {/* Path */}
            <div className="flex flex-col items-center justify-center gap-3 w-full max-w-[200px]">
              <div className="w-full flex items-center gap-4">
                <div className="h-[2px] flex-1 bg-gray-100 relative">
                  <div className="absolute left-0 top-1/2 -translate-y-1/2 size-2 rounded-full bg-gray-300" />
                </div>
                <div className={`p-3 rounded-full ${isPending ? 'bg-amber-100 text-amber-500' : 'bg-visaro-orange/10 text-visaro-orange'}`}>
                  <AirplaneInFlight size={32} weight="fill" />
                </div>
                <div className="h-[2px] flex-1 bg-gray-100 relative">
                  <div className="absolute right-0 top-1/2 -translate-y-1/2 size-2 rounded-full bg-gray-300" />
                </div>
              </div>
              <span className="text-xs font-bold text-gray-400 uppercase tracking-widest bg-gray-50 px-3 py-1 rounded-full">
                {flight.type === "return" ? "Round Trip" : "One Way"}
              </span>
            </div>

            {/* Arrival */}
            <div className="flex flex-col items-center md:items-end text-center md:text-right flex-1">
              <span className="text-gray-400 text-xs font-bold uppercase tracking-[0.2em] mb-2">Arrival</span>
              <span className="text-5xl md:text-6xl font-bold font-aeonic text-deepBlue leading-none">
                {lastLeg?.ArrivalAirportCode}
              </span>
              <span className="text-lg font-medium text-gray-600 mt-2">
                {lastLeg?.ArrivalAirportName}
              </span>
              <div className="mt-4 flex flex-col">
                <span className="text-sm font-bold text-deepBlue">
                  {moment(lastLeg?.ArrivalDate).format("ddd, MMM DD, YYYY")}
                </span>
                <span className="text-2xl font-bold text-visaro-orange">
                  {moment(lastLeg?.ArrivalDate).format("HH:mm")}
                </span>
              </div>
            </div>
          </div>

          {/* Additional Quick Details */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8 pt-8 border-t border-gray-50">
            <div className="flex flex-col gap-1">
              <span className="text-[10px] font-bold text-gray-400 uppercase tracking-widest">Flight Class</span>
              <span className="font-bold text-deepBlue">{firstLeg?.FlightClass || "Economy"}</span>
            </div>
            <div className="flex flex-col gap-1">
              <span className="text-[10px] font-bold text-gray-400 uppercase tracking-widest">Duration</span>
              <span className="font-bold text-deepBlue">
                {firstLeg?.DepartureDate && lastLeg?.ArrivalDate ? moment.duration(moment(lastLeg.ArrivalDate).diff(moment(firstLeg.DepartureDate))).humanize() : "---"}
              </span>
            </div>
            <div className="flex flex-col gap-1">
              <span className="text-[10px] font-bold text-gray-400 uppercase tracking-widest">Airline</span>
              <span className="font-bold text-deepBlue">{firstLeg?.OperatingAirlineName || firstLeg?.MarketingAirlineName || "---"}</span>
            </div>
            <div className={`flex flex-col gap-1 ${isPending ? 'text-amber-600' : 'text-green-600'}`}>
              <span className="text-[10px] font-bold text-gray-400 uppercase tracking-widest">Status</span>
              <span className="font-bold capitalize">{flight.type === "pending" ? "Awaiting Installment Approval" : "Confirmed"}</span>
            </div>
          </div>
        </div>

        {/* Pending Banner */}
        {isPending && (
          <div className="bg-amber-50 p-6 flex items-start gap-4 border-t border-amber-100 px-8 md:px-12">
            <div className="p-2 bg-white rounded-lg shadow-sm">
              <Info size={24} weight="fill" className="text-amber-500" />
            </div>
            <div className="flex flex-col gap-1">
              <h4 className="font-bold text-amber-800 text-sm">Action Required: Complete Installment Approval</h4>
              <p className="text-[11px] text-amber-700/80 leading-relaxed font-medium">
                Your flight booking is currently on hold. Our team is reviewing your installment loan application. 
                Once approved, your e-ticket will be issued automatically and sent to your registered email address.
              </p>
            </div>
          </div>
        )}
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
        {/* Travelers Section */}
        <div className="bg-white rounded-3xl border border-gray-100 p-8 shadow-sm h-full">
          <div className="flex items-center gap-3 mb-6">
            <div className="p-2.5 bg-deepBlue rounded-xl text-white">
              <Users size={20} weight="fill" />
            </div>
            <h3 className="text-lg font-bold text-deepBlue">Travelers ({flight.air_travelers?.length || 0})</h3>
          </div>
          
          <div className="flex flex-col gap-4">
            {flight.air_travelers?.map((traveler, index) => (
              <div key={index} className="flex items-center justify-between p-4 bg-gray-50 rounded-2xl border border-transparent hover:border-gray-200 transition-all">
                <div className="flex flex-col">
                  <span className="font-bold text-deepBlue text-sm">
                    {traveler.NamePrefix} {traveler.FirstName} {traveler.LastName}
                  </span>
                  <span className="text-[10px] font-medium text-gray-400 uppercase tracking-widest">
                    {traveler.PassengerTypeCode}
                  </span>
                </div>
                <CheckCircle size={20} weight="fill" className="text-green-500" />
              </div>
            ))}
            {(!flight.air_travelers || flight.air_travelers.length === 0) && (
              <div className="py-8 text-center text-gray-400 text-sm font-medium">
                No traveler information found.
              </div>
            )}
          </div>
        </div>

        {/* Flight Rules / Summary */}
        <div className="bg-white rounded-3xl border border-gray-100 p-8 shadow-sm h-full">
          <div className="flex items-center gap-3 mb-6">
            <div className="p-2.5 bg-visaro-orange rounded-xl text-white">
              <Receipt size={20} weight="fill" />
            </div>
            <h3 className="text-lg font-bold text-deepBlue">Booking Summary</h3>
          </div>

          <div className="flex flex-col gap-5">
            <div className="flex justify-between items-center py-3 border-b border-gray-50">
              <span className="text-sm font-medium text-gray-500">Booking Date</span>
              <span className="text-sm font-bold text-deepBlue text-right">
                {moment(flight.created_at).format("MMM DD, YYYY") || "N/A"}
              </span>
            </div>
            <div className="flex justify-between items-center py-3 border-b border-gray-50">
              <span className="text-sm font-medium text-gray-500">Payment Plan</span>
              <span className="text-sm font-bold text-deepBlue text-right">
                {isPending ? "Pay Later (Installments)" : "Paid in Full"}
              </span>
            </div>
            <div className="flex justify-between items-center py-3 border-b border-gray-50">
              <span className="text-sm font-medium text-gray-500">Ticket Time Limit</span>
              <span className="text-sm font-bold text-amber-600 text-right">
                {flight.ticket_time_limit ? moment(flight.ticket_time_limit).format("MMM DD, YYYY HH:mm") : "Expires after approval"}
              </span>
            </div>
            
            <div className="mt-4 p-4 bg-gray-50 rounded-2xl flex items-start gap-3">
              <Info size={18} className="text-deepBlue shrink-0 mt-0.5" weight="fill" />
              <p className="text-[11px] text-gray-500 leading-relaxed">
                Need help with your booking? Contact our support team with your reference ID: 
                <span className="text-deepBlue font-bold ml-1">#{flight.reference_number || flight.id.slice(0, 8)}</span>
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default FlightDetails;
